/**
 * Admin/ArNodes/Index.jsx — SensePoint CRUD management
 *
 * Lists every AR Node (sensePoint) belonging to offers owned by the current
 * admin account. Supports:
 *   • Status badge display (active / paused / retired)
 *   • Positioned / unpositioned indicator
 *   • Per-row delete with confirm dialog
 *   • Bulk delete of unpositioned seed nodes
 *   • Link to AR Simulator to deploy a new sensePoint
 *
 * Route: GET /admin/ar_nodes  (Admin::ArNodesController#index)
 * Props: ar_nodes[], total_count
 *
 * All mutations go to DELETE /admin/ar_nodes/:id (cookie/CSRF auth).
 */

import { useState, useCallback } from "react";
import { router } from "@inertiajs/react";

// ── Status styling ───────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:  { bg: "#064E3B", fg: "#10B981", label: "Active"  },
  paused:  { bg: "#78350F", fg: "#F59E0B", label: "Paused"  },
  retired: { bg: "#1F1F2E", fg: "#6B7280", label: "Retired" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.retired;
  return (
    <span style={{
      display:       "inline-block",
      padding:       "2px 9px",
      borderRadius:  10,
      fontSize:      11,
      fontWeight:    700,
      background:    s.bg,
      color:         s.fg,
      border:        `1px solid ${s.fg}66`,
      whiteSpace:    "nowrap",
    }}>
      {s.label}
    </span>
  );
}

// ── Position indicator ───────────────────────────────────────────────────────

function PositionBadge({ positioned, anchorRef }) {
  if (positioned) {
    return <span style={styles.posBadgeGood} title="GPS confirmed">📍 Positioned</span>;
  }
  if (anchorRef) {
    return <span style={styles.posBadgeWarn} title={`Anchor: ${anchorRef}`}>⚓ {anchorRef}</span>;
  }
  return <span style={styles.posBadgeMiss}>⚠ Unplaced</span>;
}

// ── Main component ───────────────────────────────────────────────────────────

export default function ArNodesIndex({ ar_nodes = [], total_count = 0 }) {
  const [nodes,        setNodes]       = useState(ar_nodes);
  const [deleting,     setDeleting]    = useState(null);   // id being deleted
  const [confirmId,    setConfirmId]   = useState(null);   // id waiting for confirm
  const [bulkConfirm,  setBulkConfirm] = useState(false);
  const [filterStatus, setFilter]      = useState("all");
  const [search,       setSearch]      = useState("");
  const [error,        setError]       = useState(null);

  // ── Delete one node ────────────────────────────────────────────────────────

  const handleDelete = useCallback(async (id) => {
    setDeleting(id);
    setError(null);
    try {
      const meta = document.querySelector('meta[name="csrf-token"]');
      const res  = await fetch(`/admin/ar_nodes/${id}`, {
        method:  "DELETE",
        headers: {
          "X-CSRF-Token": meta?.content || "",
          "Accept":       "application/json",
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setNodes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(null);
      setConfirmId(null);
    }
  }, []);

  // ── Bulk delete unpositioned nodes ─────────────────────────────────────────

  const handleBulkDelete = useCallback(async () => {
    setBulkConfirm(false);
    const unplaced = nodes.filter((n) => !n.positioned && !n.anchor_node_ref);
    setError(null);
    for (const n of unplaced) {
      await handleDelete(n.id);
    }
  }, [nodes, handleDelete]);

  // ── Filter / search ────────────────────────────────────────────────────────

  const visible = nodes.filter((n) => {
    if (filterStatus !== "all" && n.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        n.service_label?.toLowerCase().includes(q) ||
        n.offer?.name?.toLowerCase().includes(q) ||
        String(n.id).includes(q)
      );
    }
    return true;
  });

  const unplacedCount = nodes.filter((n) => !n.positioned && !n.anchor_node_ref).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={styles.root}>
      {/* Header */}
      {/* Back nav */}
      <div style={styles.backRow}>
        <a href="/admin/queue_map_monitor" style={styles.backLink}>← Map Monitor</a>
        <a href="/admin/ar_simulator"      style={styles.backLink}>⬡ AR Simulator</a>
      </div>

      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>⬡ SensePoints</h1>
          <p style={styles.sub}>
            {total_count} total · {nodes.length} loaded
            {nodes.length !== total_count ? ` (filtered: ${visible.length})` : ""}
          </p>
        </div>
        <div style={styles.headerActions}>
          <a href="/admin/ar_simulator" style={styles.btnPrimary}>
            + Deploy New SensePoint
          </a>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div style={styles.errorBanner}>
          <span>⚠ {error}</span>
          <button onClick={() => setError(null)} style={styles.errorDismiss}>✕</button>
        </div>
      )}

      {/* Bulk-delete unplaced seeds banner */}
      {unplacedCount > 0 && !bulkConfirm && (
        <div style={styles.warnBanner}>
          <span>
            <strong>{unplacedCount}</strong> unplaced node{unplacedCount > 1 ? "s" : ""} with
            no GPS or anchor — likely seed data.
          </span>
          <button onClick={() => setBulkConfirm(true)} style={styles.btnWarn}>
            Delete all unplaced
          </button>
        </div>
      )}
      {bulkConfirm && (
        <div style={styles.confirmBanner}>
          <span>Delete all {unplacedCount} unplaced nodes? This cannot be undone.</span>
          <button onClick={handleBulkDelete}  style={styles.btnDanger}>Yes, delete</button>
          <button onClick={() => setBulkConfirm(false)} style={styles.btnGhost}>Cancel</button>
        </div>
      )}

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <input
          type="search"
          placeholder="Search by name, offer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchBox}
        />
        <div style={styles.filterGroup}>
          {["all", "active", "paused", "retired"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                ...styles.filterBtn,
                background: filterStatus === s ? "rgba(99,102,241,0.2)" : "transparent",
                borderColor: filterStatus === s ? "#6366F1" : "#1F2937",
                color:       filterStatus === s ? "#A5B4FC"  : "#6B7280",
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {["ID", "SensePoint", "Offer", "Price", "Cap.", "Avail.", "Status", "Position", "Published", ""].map((h) => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={10} style={styles.emptyCell}>
                  No sensePoints match your filter.
                </td>
              </tr>
            ) : (
              visible.map((n) => {
                const isDeleting = deleting === n.id;
                const isConfirm  = confirmId === n.id;
                return (
                  <tr key={n.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.idChip}>#{n.id}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.nodeName}>⬡ {n.service_label}</div>
                      {n.operator_note && (
                        <div style={styles.noteText} title={n.operator_note}>
                          {n.operator_note.slice(0, 40)}{n.operator_note.length > 40 ? "…" : ""}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.offerName}>{n.offer?.name || "—"}</span>
                    </td>
                    <td style={{ ...styles.td, fontVariantNumeric: "tabular-nums" }}>
                      {n.price_cents > 0 ? `$${(n.price_cents / 100).toFixed(2)}` : <span style={{ color: "#10B981" }}>Free</span>}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{n.capacity}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <span style={{ color: n.availability === 0 ? "#EF4444" : "#D1D5DB" }}>
                        {n.availability}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <StatusBadge status={n.status} />
                    </td>
                    <td style={styles.td}>
                      <PositionBadge positioned={n.positioned} anchorRef={n.anchor_node_ref} />
                    </td>
                    <td style={{ ...styles.td, fontSize: 11, color: "#6B7280", whiteSpace: "nowrap" }}>
                      {n.published_at ? new Date(n.published_at).toLocaleDateString() : "—"}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {isConfirm ? (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <button
                            onClick={() => handleDelete(n.id)}
                            disabled={isDeleting}
                            style={styles.btnDanger}
                          >
                            {isDeleting ? "…" : "Confirm"}
                          </button>
                          <button
                            onClick={() => setConfirmId(null)}
                            style={styles.btnGhost}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmId(n.id)}
                          disabled={isDeleting}
                          style={styles.btnDeleteRow}
                          title="Delete this sensePoint"
                        >
                          🗑
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer info */}
      <p style={styles.footNote}>
        SensePoints are deployed from the{" "}
        <a href="/admin/ar_simulator" style={styles.link}>AR Simulator</a>. Drag unplaced
        markers to their final location in the{" "}
        <a href="/admin/queue_map_monitor" style={styles.link}>Map Monitor → Property</a> tab.
      </p>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  backRow: {
    display:      "flex",
    gap:          10,
    marginBottom: 18,
  },
  backLink: {
    display:        "inline-flex",
    alignItems:     "center",
    padding:        "6px 16px",
    borderRadius:   7,
    background:     "#111827",
    border:         "1px solid #374151",
    color:          "#D1D5DB",
    textDecoration: "none",
    fontSize:       12,
    fontWeight:     600,
    transition:     "color 0.15s, border-color 0.15s",
  },
  root: {
    padding:    "28px 32px",
    maxWidth:   1200,
    margin:     "0 auto",
    fontFamily: "system-ui, -apple-system, sans-serif",
    color:      "#E0E0F0",
  },
  header: {
    display:        "flex",
    alignItems:     "flex-start",
    justifyContent: "space-between",
    marginBottom:   24,
    gap:            16,
    flexWrap:       "wrap",
  },
  h1: {
    margin:     0,
    fontSize:   22,
    fontWeight: 700,
    color:      "#E0E0F0",
  },
  sub: {
    margin:   "4px 0 0",
    fontSize: 13,
    color:    "#6B7280",
  },
  headerActions: {
    display: "flex",
    gap:     10,
    flexWrap: "wrap",
  },
  btnPrimary: {
    display:        "inline-flex",
    alignItems:     "center",
    padding:        "8px 18px",
    borderRadius:   8,
    background:     "rgba(99,102,241,0.15)",
    border:         "1px solid #6366F1",
    color:          "#A5B4FC",
    textDecoration: "none",
    fontSize:       13,
    fontWeight:     600,
    cursor:         "pointer",
  },
  errorBanner: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    background:     "#450A0A",
    border:         "1px solid #EF4444",
    borderRadius:   8,
    padding:        "10px 14px",
    marginBottom:   16,
    fontSize:       13,
    color:          "#FCA5A5",
    gap:            12,
  },
  errorDismiss: {
    background: "transparent",
    border:     "none",
    color:      "#FCA5A5",
    cursor:     "pointer",
    fontSize:   16,
    padding:    0,
  },
  warnBanner: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    flexWrap:       "wrap",
    background:     "#422006",
    border:         "1px solid #F59E0B",
    borderRadius:   8,
    padding:        "10px 14px",
    marginBottom:   16,
    fontSize:       13,
    color:          "#FCD34D",
    gap:            12,
  },
  confirmBanner: {
    display:     "flex",
    alignItems:  "center",
    flexWrap:    "wrap",
    background:  "#450A0A",
    border:      "1px solid #EF4444",
    borderRadius: 8,
    padding:     "10px 14px",
    marginBottom: 16,
    fontSize:    13,
    color:       "#FCA5A5",
    gap:         12,
  },
  btnWarn: {
    padding:      "5px 14px",
    borderRadius: 6,
    background:   "rgba(245,158,11,0.15)",
    border:       "1px solid #F59E0B",
    color:        "#FCD34D",
    fontSize:     12,
    fontWeight:   600,
    cursor:       "pointer",
    whiteSpace:   "nowrap",
  },
  btnDanger: {
    padding:      "5px 14px",
    borderRadius: 6,
    background:   "rgba(239,68,68,0.15)",
    border:       "1px solid #EF4444",
    color:        "#FCA5A5",
    fontSize:     12,
    fontWeight:   600,
    cursor:       "pointer",
    whiteSpace:   "nowrap",
  },
  btnGhost: {
    padding:      "5px 14px",
    borderRadius: 6,
    background:   "transparent",
    border:       "1px solid #374151",
    color:        "#9CA3AF",
    fontSize:     12,
    cursor:       "pointer",
    whiteSpace:   "nowrap",
  },
  toolbar: {
    display:      "flex",
    alignItems:   "center",
    gap:          12,
    marginBottom: 16,
    flexWrap:     "wrap",
  },
  searchBox: {
    flex:         1,
    minWidth:     200,
    maxWidth:     320,
    padding:      "7px 12px",
    borderRadius: 8,
    border:       "1px solid #1F2937",
    background:   "#0D1117",
    color:        "#E0E0F0",
    fontSize:     13,
    outline:      "none",
  },
  filterGroup: {
    display: "flex",
    gap:     6,
  },
  filterBtn: {
    padding:      "5px 14px",
    borderRadius: 6,
    border:       "1px solid #1F2937",
    fontSize:     12,
    cursor:       "pointer",
    transition:   "all 0.15s",
  },
  tableWrap: {
    overflowX:    "auto",
    borderRadius: 10,
    border:       "1px solid #1F2937",
  },
  table: {
    width:           "100%",
    borderCollapse:  "collapse",
    fontSize:        13,
  },
  th: {
    padding:         "10px 14px",
    textAlign:       "left",
    color:           "#6B7280",
    fontWeight:      600,
    fontSize:        11,
    textTransform:   "uppercase",
    letterSpacing:   "0.05em",
    borderBottom:    "1px solid #1F2937",
    background:      "#0A0D14",
    whiteSpace:      "nowrap",
  },
  tr: {
    borderBottom: "1px solid #111827",
  },
  td: {
    padding:     "11px 14px",
    verticalAlign: "middle",
    color:       "#D1D5DB",
  },
  emptyCell: {
    padding:   "40px 0",
    textAlign: "center",
    color:     "#374151",
    fontSize:  14,
  },
  idChip: {
    display:      "inline-block",
    padding:      "1px 7px",
    borderRadius: 5,
    background:   "#111827",
    border:       "1px solid #1F2937",
    fontFamily:   "monospace",
    fontSize:     11,
    color:        "#6B7280",
  },
  nodeName: {
    fontWeight: 600,
    color:      "#C4B5FD",
    fontSize:   13,
  },
  noteText: {
    fontSize:  11,
    color:     "#6B7280",
    marginTop: 2,
  },
  offerName: {
    fontSize:   12,
    color:      "#9CA3AF",
    fontStyle:  "italic",
  },
  posBadgeGood: {
    display:      "inline-block",
    padding:      "2px 7px",
    borderRadius: 8,
    fontSize:     11,
    background:   "#052E16",
    color:        "#10B981",
    border:       "1px solid #10B98133",
    whiteSpace:   "nowrap",
  },
  posBadgeWarn: {
    display:      "inline-block",
    padding:      "2px 7px",
    borderRadius: 8,
    fontSize:     11,
    background:   "#422006",
    color:        "#F59E0B",
    border:       "1px solid #F59E0B33",
    whiteSpace:   "nowrap",
  },
  posBadgeMiss: {
    display:      "inline-block",
    padding:      "2px 7px",
    borderRadius: 8,
    fontSize:     11,
    background:   "#1C1917",
    color:        "#6B7280",
    border:       "1px solid #37415133",
    whiteSpace:   "nowrap",
  },
  btnDeleteRow: {
    padding:      "4px 8px",
    borderRadius: 5,
    background:   "transparent",
    border:       "1px solid #1F2937",
    color:        "#6B7280",
    fontSize:     14,
    cursor:       "pointer",
    transition:   "all 0.15s",
  },
  footNote: {
    marginTop:  20,
    fontSize:   12,
    color:      "#374151",
    lineHeight: 1.6,
  },
  link: {
    color:          "#818CF8",
    textDecoration: "none",
  },
};
